<template>
  <div class="flex h-full flex-grow flex-col justify-between gap-2">
    <div
      class="flex flex-col content-center items-center justify-center gap-2 transition-all duration-300"
      :class="{
        'h-full w-full': !isCollapsed,
        'absolute mb-2 py-3 px-4 rounded border border-prim-300 bg-pure-white': isCollapsed,
        'bottom-full opacity-100': isCollapsed && (showPopup || probingLoading),
        '-z-10 bottom-0 opacity-0': isCollapsed && !showPopup
      }"
      :style="isCollapsed ? { left: '50%', transform: 'translateX(-50%)' } : {}"
    >
      <div
        :id="`probing-scroll-${measurement.id}`"
        class="pb-4 w-full flex snap-x-mandatory gap-4 overflow-x-auto scrolling-touch scrollbar-lg"
        :class="{
          'max-w-52': sessionStatus === 'draft',
          'max-w-64': sessionStatus !== 'draft'
        }"
        dir="ltr"
        @scroll="onScroll"
      >
        <div
          v-for="(probingCircles, idx) in probingCirclesPages"
          :key="`${measurement.id}-probing-circles-${idx + 1}`"
          :id="`${measurement.id}-probing-circles-${idx + 1}`"
          class="flex flex-shrink-0 justify-center snap-center"
          :class="{
            'w-full ': !isCollapsed,
            'w-52': isCollapsed && sessionStatus === 'draft',
            'w-64': isCollapsed && sessionStatus !== 'draft'
          }"
        >
          <div
            class="flex w-full flex-wrap content-center items-start justify-center gap-x-2 gap-y-2"
          >
            <div
              v-for="box in probingCircles"
              :key="`${box.key}_${box.value}`"
              class="flex flex-shrink-0 items-center justify-center rounded-full cursor-pointer transition-all duration-300"
              :class="{
                'h-8 w-8': sessionStatus === 'draft',
                'h-10 w-10': sessionStatus !== 'draft',
                'cursor-wait': removeProbingLoading,
                'pointer-events-none':
                  measurement.submitted_at ||
                  box.value === 'empty' ||
                  box.value === 'removing' ||
                  sessionStatus !== 'ongoing',
                'border-2 border-dashed border-slate-500 bg-slate-200':
                  box.value === 'empty',
                'bg-pure-white': box.value === 'removing',
                'bg-lime-500': box.value === true,
                'bg-tomato-700': box.value === false
              }"
              @click="onRemove(box)"
            >
              <div
                v-if="box.value === 'removing'"
                class="blum-loader-btn"
              ></div>
              <BlIcon
                v-else
                icon="delete"
                color="white"
                class="h-6 w-6 opacity-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isCollapsed"
        class="flex h-3 items-center justify-center gap-2"
      >
        <div
          v-for="n in pageCount"
          :key="n"
          :class="{ 'bg-slate-700': n === page, 'bg-slate-400': n !== page }"
          class="h-3 w-3 rounded-full cursor-pointer transition-all duration-300"
          @click="onChangePage(n)"
        ></div>
      </div>
      <div
        v-if="measurement.submitted_at && !isCollapsed"
        class="flex w-60 justify-center"
      >
        <StatusChip :status="measurement.marked_as" />
      </div>
    </div>

    <div
      class="flex-shrink-0 space-y-2"
      :class="{ 'relative z-10 h-full': isCollapsed }"
    >
      <div
        v-if="measurement.submitted_at && isCollapsed"
        class="absolute -top-1 left-1/2 flex -translate-x-1/2 justify-center"
      >
        <StatusChip :status="measurement.marked_as" />
      </div>
      <div
        v-if="!isCollapsed"
        class="mb-4 flex h-3 items-center justify-center gap-2"
      >
        <div
          v-for="n in pageCount"
          :key="n"
          :class="{ 'bg-slate-700': n === page, 'bg-slate-400': n !== page }"
          class="h-3 w-3 rounded-full cursor-pointer transition-all duration-300"
          @click="onChangePage(n)"
        ></div>
      </div>
      <div
        class="flex flex-col"
        :class="{ 'gap-4': !isCollapsed, 'gap-0 pt-2': isCollapsed }"
      >
        <div
          class="flex items-center justify-center"
          :class="{
            'scale-90 gap-3': isCollapsed,
            'gap-3 xl:gap-4': !isCollapsed
          }"
        >
          <div
            class="flex flex-shrink-0 items-center justify-center rounded-full cursor-pointer transition-all duration-300"
            :class="{
              'h-16 w-16': sessionStatus === 'draft',
              'h-18 w-18 xl:h-20 xl:w-20': sessionStatus !== 'draft',
              'cursor-wait': probingLoading,
              'pointer-events-none':
                measurement.submitted_at || sessionStatus !== 'ongoing',
              'bg-tomato-900': reduceProbingLoading,
              'bg-slate-500': measurement.submitted_at,
              'bg-tomato-700':
                !measurement.submitted_at && !reduceProbingLoading
            }"
            @click="onAdd(false)"
          >
            <BlIcon icon="close" color="white" class="h-10 w-10" />
          </div>
          <div
            class="flex flex-shrink-0 items-center justify-center rounded-full cursor-pointer transition-all duration-300"
            :class="{
              'h-16 w-16': sessionStatus === 'draft',
              'h-18 w-18 xl:h-20 xl:w-20': sessionStatus !== 'draft',
              'cursor-wait': probingLoading,
              'pointer-events-none':
                measurement.submitted_at || sessionStatus !== 'ongoing',
              'bg-lime-700': plusProbingLoading,
              'bg-slate-500': measurement.submitted_at,
              'bg-lime-500': !measurement.submitted_at && !plusProbingLoading
            }"
            @click="onAdd(true)"
          >
            <BlIcon icon="check" color="white" class="h-16 w-16" />
          </div>
          <div
            v-if="
              !measurement.submitted_at &&
                Object.keys(measurement.results).length >=
                  (measurement.target.probing_number_of_trial || 0)
            "
            class="flex flex-shrink-0 items-center justify-center rounded-full bg-light-purple hover:bg-dark-purple-200 cursor-pointer transition-all duration-300"
            :class="{
              'h-16 w-16': sessionStatus === 'draft',
              'h-18 w-18 xl:h-20 xl:w-20': sessionStatus !== 'draft',
              'pointer-events-none': sessionStatus !== 'ongoing'
            }"
            @click="onSubmitProbing"
          >
            <BlTextSubtitle3 text="Submit" class="text-pure-white" />
          </div>
        </div>
      </div>
      <div v-if="!isCollapsed" class="pb-3 flex-shrink-0 space-y-2">
        <div class="flex items-center gap-2">
          <div class="w-9 flex-shrink-0">
            <BlTextBody2 text="Score" />
          </div>
          <div class="h-6 flex items-center rounded-full bg-lime-200 px-2">
            <BlTextBody2
              :text="probingScore.toFixed(0)"
              class="text-lime-700"
            />
          </div>
        </div>
        <div class="flex gap-2">
          <div class="w-9 flex-shrink-0">
            <BlTextBody2 text="Goal" />
          </div>
          <div>
            <BlTextBody2 text="score ≥" />
            <BlTextBody2 :text="`${measurement.target.probing_goal}%`" />
            <BlTextBody2 text="in minimum" />
            <BlTextBody2 :text="measurement.target.probing_number_of_trial" />
            <BlTextBody2 text="trial(s)" />
          </div>
        </div>
        <div
          v-if="sessionStatus === 'draft' && measurement.target.probing_enable"
          class="z-10 w-full py-2 px-4 rounded-full bg-pure-white flex items-center justify-between"
        >
          <div>
            <BlTextSubtitle3 text="Set as probing" class="text-lime-700" />
          </div>
          <div v-if="measurement.type" class="flex flex-shrink-0 gap-1">
            <BlTextSubtitle3
              :text="measurement.type.includes('Probing') ? 'Yes' : 'No'"
              class="text-lime-700"
            />
            <BlInputToggle
              :checked="measurement.type.includes('Probing')"
              color="lime"
              :loading="switchLoading"
              @changed="onChangeToPercentage"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showPanel"
      class="absolute left-0 top-0 z-10 h-full w-full rounded border-2 border-white overflow-y-auto"
      :style="{
        background: 'linear-gradient(180deg, #F2F8CF 0%, #FFFFFF 100%)'
      }"
    >
      <div
        v-if="showCelebration"
        class="grid h-full w-full place-content-center"
      >
        <BlGif gif="celebration" class="rounded-full h-72 w-72" />
      </div>
      <div
        v-if="!showCelebration"
        class="flex h-full w-full flex-col items-center justify-center lg:justify-start xl:justify-center gap-2"
      >
        <BlIcon
          v-if="isProbingPassed"
          icon="prob-passed"
          class="w-full absolute bottom-0 z-0"
        />
        <BlIcon
          v-if="!isProbingPassed"
          icon="prob-failed"
          class="w-20"
          :class="{
            'absolute top-2 right-2 z-0 opacity-50':
              measurement.target.status === 'mastered'
          }"
        />
        <div class="py-4 px-4 w-full flex flex-col gap-3">
          <div v-if="isProbingPassed" class="space-y-1 text-center">
            <BlTextSubtitle2
              text="This target has probed out."
              class="text-lime-800"
            />
            <BlTextSubtitle3
              text="What action would you like to take?"
              class="text-lime-700"
            />
          </div>
          <div v-if="!isProbingPassed" class="space-y-1 text-center">
            <BlTextSubtitle2
              text="This target doesn't meet the goal."
              class="text-lime-800"
            />
            <div>
              <BlTextSubtitle3
                v-if="measurement.target.status === 'mastered'"
                text="What action would you like to take?"
                class="text-lime-700"
              />
              <BlTextSubtitle3
                v-else
                text="Do you want to begin teaching now or postpone it to the next session?"
                class="text-lime-700"
              />
            </div>
          </div>
          <div
            v-for="opt in probingActionOptions"
            :key="opt.id"
            class="p-3 flex flex-col items-center justify-center gap-2 rounded-lg border bg-pure-white text-center cursor-pointer transition-all duration-300"
            :class="{
              'border-lime-600': (probingAction && probingAction.id) === opt.id,
              'border-pure-white':
                (probingAction && probingAction.id) !== opt.id
            }"
            :style="{ boxShadow: '0px 4px 8px -2px #B9D84333' }"
            @click="probingAction = opt"
          >
            <div class="flex items-center gap-2">
              <BlTextSubtitle3 :text="opt.title" />
              <StatusChip v-if="opt.status" :status="opt.status" />
            </div>
            <BlTextBody2 v-if="opt.message" :text="opt.message" />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <BlInputButton
              type="plain-lime"
              label="Back to Session"
              @clicked="showPanel = false"
            />
            <BlInputButton
              type="lime"
              label="Save"
              :loading="saveLoading"
              :disabled="!probingAction"
              @clicked="onSave"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showPanel && measurement.is_fixed"
      class="absolute -bottom-20 left-0 h-20 w-full bg-prim-300"
    ></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import StatusChip from "../../clients/target/StatusChip.vue";

import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlGif from "../../BlGif.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";

export default {
  name: "MeasurementCardProbing",
  components: {
    StatusChip,
    //
    BlTextTitle3,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlGif,
    BlInputButton,
    BlInputToggle
  },
  props: {
    measurement: { type: Object, required: true },
    sessionStatus: { type: String, required: true },
    isCollapsed: { type: Boolean, default: false }
    // @toggle-collapsed
  },
  data() {
    return {
      showPopup: false,
      popupTimeout: null,
      page: 1,
      probingLoading: false,
      plusProbingLoading: false,
      reduceProbingLoading: false,
      removeProbingLoading: false,
      showPanel: false,
      //
      isProbingPassed: false,
      showCelebration: false,
      //
      probingAction: null,
      saveLoading: false,
      //
      switchLoading: false
    };
  },
  computed: {
    ...mapGetters(["viewMode"]),
    perPage() {
      return 20;
    },
    pageCount() {
      const results = Object.keys(this.measurement.results).length || 0;
      const trial = this.measurement.target.probing_number_of_trial || 0;
      let circles = results || trial;
      return Math.ceil(circles / this.perPage);
    },
    probingCirclesPages() {
      const results = this.measurement.results;
      const trial = this.measurement.target.probing_number_of_trial || 0;
      const circles = [];
      for (let idx = 0; idx < trial; idx++) {
        circles.push({ key: idx, value: "empty" });
      }
      for (let key in results) {
        const idx = circles.findIndex(i => Number(i.key) === Number(key));
        if (idx > -1) circles[idx].value = results[key];
        else circles.push({ key, value: results[key] });
      }
      const res = [];
      for (let idx = 1; idx <= this.pageCount; idx++) {
        const start = (idx - 1) * this.perPage;
        const end = idx * this.perPage;
        const arr = [...circles.slice(start, end)];
        res.push(arr);
      }
      return res;
    },
    probingScore() {
      const results = this.measurement.results;
      const trials = Object.values(results).length;
      const totalSuccess = Object.values(results).filter(i => i).length;
      return (totalSuccess / trials) * 100 || 0;
    },
    //
    probingActionOptions() {
      const passed = [
        {
          id: "pass_and_mastered",
          visible: true,
          marked_as: "mastered",
          title: "Marked as",
          status: "mastered",
          message:
            "No further teaching needed. The target will be added to the cumulative graph."
        },
        {
          id: "pass_and_conduct_teaching",
          visible: false,
          marked_as: "in_progress",
          title:
            this.measurement.target.status === "pending"
              ? "Marked as"
              : "Keep as",
          status: "in_progress",
          message: "Conduct teaching immediately."
        },
        {
          id: "pass_and_postpone_teaching",
          visible: true,
          marked_as: "in_progress",
          title:
            this.measurement.target.status === "pending"
              ? "Marked as"
              : "Keep as",
          status: "in_progress",
          message: "Postpone teaching to the next session."
        }
      ];
      const failed = [
        {
          id: "fail_and_start_teaching",
          visible: false,
          marked_as: "in_progress",
          title: "Start teaching now",
          status: null,
          message: null
        },
        {
          id: "fail_and_next",
          visible: true,
          marked_as: "in_progress",
          title: "Next session",
          status: null,
          message: null
        }
      ];
      const passedInMastered = [
        {
          id: "pass_and_keep_mastered",
          visible: true,
          marked_as: "mastered",
          title: "Keep as",
          status: "mastered",
          message: "No further action is required."
        },
        {
          id: "pass_and_conduct_teaching",
          visible: false,
          marked_as: "in_progress",
          title: "Reopen target to",
          status: "in_progress",
          message: "Conduct teaching immediately."
        },
        {
          id: "pass_and_postpone_teaching",
          visible: true,
          marked_as: "in_progress",
          title: "Reopen target to",
          status: "in_progress",
          message: "Postpone teaching to the next session."
        }
      ];
      const failedInMastered = [
        {
          id: "fail_and_restart_teaching",
          visible: false,
          marked_as: "in_progress",
          title: "Reopen target to",
          status: "in_progress",
          message: "Restart teaching immediately."
        },
        {
          id: "fail_and_postpone_teaching",
          visible: true,
          marked_as: "in_progress",
          title: "Reopen target to",
          status: "in_progress",
          message: "Postpone teaching to the next session."
        },
        {
          id: "fail_and_keep_mastered",
          visible: true,
          marked_as: "mastered",
          title: "Keep as",
          status: "mastered",
          message: "No further action is required."
        }
      ];
      if (this.measurement.target.status === "mastered") {
        return this.isProbingPassed ? passedInMastered : failedInMastered;
      } else {
        return this.isProbingPassed ? passed : failed;
      }
    }
  },
  watch: {
    isCollapsed() {
      setTimeout(() => {
        const id = `${this.measurement.id}-probing-circles-${1}`;
        const circles = document.getElementById(id);
        if (circles) {
          circles.scrollIntoView({ behavior: "smooth", inline: "center" });
        }
      }, 300);
    },
    showPanel(val) {
      if (val) {
        this.$emit("toggle-collapsed", false);
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions([
      "getMeasurements",
      "updateMeasurementResults",
      "updateMeasurementMarkProbing",
      "updateMeasurement",
      "createMeasurement",
      "deleteMeasurement"
    ]),
    onDisplayPopup() {
      if (!this.isCollapsed) return;
      this.showPopup = true;
      clearTimeout(this.popupTimeout);
      this.popupTimeout = setTimeout(() => {
        this.showPopup = false;
      }, 3000);
    },
    onScroll(e) {
      this.onDisplayPopup();
      const offsetWidth = e.currentTarget.offsetWidth;
      const scrollLeft = e.currentTarget.scrollLeft;
      const current = Math.floor(scrollLeft / offsetWidth) + 1;
      this.page = current;
    },
    onChangePage(num) {
      const id = `${this.measurement.id}-probing-circles-${num}`;
      const circles = document.getElementById(`${id}`);
      if (circles) {
        circles.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    // ongoing
    async onAdd(bool) {
      if (this.sessionStatus !== "ongoing") return;
      if (this.probingLoading) return;
      this.onDisplayPopup();
      const params = {
        id: this.measurement.id,
        results: { ...this.measurement.results }
      };
      const length = Object.keys(params.results).length;
      params.results[length] = bool;

      this.probingLoading = true;
      if (bool) this.plusProbingLoading = true;
      else this.reduceProbingLoading = true;
      const { success } = await this.updateMeasurementResults(params);
      this.probingLoading = false;
      this.plusProbingLoading = false;
      this.reduceProbingLoading = false;

      if (!success) return;
      this.onDisplayPopup();
    },
    async onRemove(circle) {
      if (this.sessionStatus !== "ongoing") return;
      if (this.probingLoading) return;
      this.onDisplayPopup();
      const params = {
        id: this.measurement.id,
        results: {}
      };
      const res = this.measurement.results;
      res[circle.key] = "removing";
      let idx = 0;
      for (let key in res) {
        if (res[key] === true || res[key] === false) {
          params.results[idx] = res[key];
          idx++;
        }
      }
      this.probingLoading = true;
      this.removeProbingLoading = true;
      const { success } = await this.updateMeasurementResults(params);
      this.probingLoading = false;
      this.removeProbingLoading = false;

      if (!success) return;
      this.onDisplayPopup();
    },
    async onSubmitProbing() {
      if (this.sessionStatus !== "ongoing") return;
      this.probingAction = null;
      this.showPanel = true;
      this.isProbingPassed =
        this.probingScore >= (this.measurement.target.probing_goal || 0);
      if (this.isProbingPassed) {
        this.showCelebration = true;
        setTimeout(() => {
          this.showCelebration = false;
        }, 2000);
      }
    },
    async onSave() {
      if (this.sessionStatus !== "ongoing") return;
      if (this.saveLoading) return;
      const params = {
        id: this.measurement.id,
        visible: this.probingAction.visible,
        marked_as: this.probingAction.marked_as
      };

      this.saveLoading = true;
      const { success } = await this.updateMeasurementMarkProbing(params);
      this.saveLoading = false;
      if (!success) return;
      this.showPanel = false;
    },
    // draft
    async onChangeToPercentage() {
      if (this.sessionStatus !== "draft") return;
      if (this.switchLoading) return;
      const checked = this.measurement.type.includes("Probing");
      const temp = {
        session_id: this.measurement.session_id,
        target_id: this.measurement.target_id,
        measurement_id: this.measurement.id,
        position: this.measurement.position,
        is_fixed: this.measurement.is_fixed
      };

      if (!checked) {
        const updateParams = {
          skip_action: true,
          id: temp.measurement_id,
          measurement: {
            visible: false
          }
        };

        this.switchLoading = true;
        const { success: successUpdate } = await this.updateMeasurement(
          updateParams
        );
        if (!successUpdate) {
          this.switchLoading = false;
          return;
        }

        const createParams = {
          id: temp.session_id,
          skip_action: true,
          target_id: temp.target_id,
          measurement: {
            type: "Measurement::Probing",
            position: temp.position,
            is_fixed: temp.is_fixed
          }
        };
        const { success: succesCreate } = await this.createMeasurement(
          createParams
        );
        if (!succesCreate) {
          this.switchLoading = false;
          return;
        }
      } else {
        const params = {
          id: temp.measurement_id,
          skip_action: true
        };

        this.switchLoading = true;
        const { success } = await this.deleteMeasurement(params);
        if (!success) {
          this.switchLoading = false;
          return;
        }
      }
      setTimeout(async () => {
        await this.getMeasurements({ id: temp.session_id });
        this.switchLoading = false;
      }, 300);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/card/Probing.vue